import React, { Suspense, lazy } from "react";
import ScrollToTop from "@/components/ScrollToTop.jsx";
import TitleManager from "@/components/TitleManager.jsx";
import ReferralCapture from "@/components/marketing/ReferralCapture.jsx";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, Outlet } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { PROGRAMMATIC_PAGES } from '@/lib/programmaticSeo';

// Keep Landing eagerly loaded to preserve SEO prerendering quality.
import Landing from './Landing';
// Public SEO pages are prerendered (avoid Suspense fallback in static HTML).
import Tarifs from './Tarifs';
import FAQ from './FAQ';
import Contact from './Contact';
import MentionsLegales from './MentionsLegales';
import CGU from './CGU';
import Confidentialite from './Confidentialite';
import Cookies from './Cookies';
import Disclaimer from './Disclaimer';

// Route-based code-splitting: keep the initial bundle light.
// Pages are loaded only when needed (improves Lighthouse Mobile/Desktop).
// Layout is heavy (auth + supabase + notifications). Keep it out of the public/SEO bundle.
const AppLayout = lazy(() => import('./Layout.jsx'));

const AIReports = lazy(() => import('./AIReports'));
const AdminReminders = lazy(() => import('./AdminReminders'));
const Billing = lazy(() => import('./Billing'));
// (CGU is imported eagerly above)
const Charges = lazy(() => import('./Charges'));
const Tresorerie = lazy(() => import('./Tresorerie'));
// (Confidentialite / Contact / Cookies are imported eagerly above)
const Dashboard = lazy(() => import('./Dashboard'));
const Deadlines = lazy(() => import('./Deadlines'));
// (Disclaimer is imported eagerly above)
const Envelopes = lazy(() => import('./Envelopes'));
const Exports = lazy(() => import('./Exports'));
// (FAQ is imported eagerly above)
const Factures = lazy(() => import('./Factures'));
const Documents = lazy(() => import('./Documents'));
const Goals = lazy(() => import('./Goals'));
// (Landing is imported eagerly above)
// (MentionsLegales is imported eagerly above)
const Login = lazy(() => import('./Login'));
const Register = lazy(() => import('./Register'));
const CheckEmail = lazy(() => import('./CheckEmail'));
const AuthCallback = lazy(() => import('./AuthCallback'));
const ResetPassword = lazy(() => import('./ResetPassword'));
const Logout = lazy(() => import('./Logout'));
const AmbassadorRedirect = lazy(() => import('./AmbassadorRedirect'));
const Onboarding = lazy(() => import('./Onboarding'));
const PaymentSuccess = lazy(() => import('./PaymentSuccess'));
const Previsionnel = lazy(() => import('./Previsionnel'));
const Reports = lazy(() => import('./Reports'));
const Revenues = lazy(() => import('./Revenues'));
const LivreRecettes = lazy(() => import('./LivreRecettes'));
const Salaire = lazy(() => import('./Salaire'));
const Settings = lazy(() => import('./Settings'));
const Simulations = lazy(() => import('./Simulations'));
const Unsubscribe = lazy(() => import('./Unsubscribe'));
const ProgrammaticPage = lazy(() => import('./ProgrammaticPage'));

const Contrats = lazy(() => import('./Contrats'));
const Contrat = lazy(() => import('./Contrat'));
const ContratPublic = lazy(() => import('./ContratPublic'));
const DevisPublic = lazy(() => import('./DevisPublic'));

function PageFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-slate-500">
      Chargement…
    </div>
  );
}

function ProtectedShell() {
  const location = useLocation();
  const currentPage = _getCurrentPage(location.pathname);

  return (
    <Suspense fallback={<PageFallback />}>
      <AppLayout currentPageName={currentPage}>
        <Outlet />
      </AppLayout>
    </Suspense>
  );
}

const PAGES = {
    
    AIReports: AIReports,
    AdminReminders: AdminReminders,
    
    Billing: Billing,
    
    CGU: CGU,
    
    Charges: Charges,
    Tresorerie: Tresorerie,
    
    Confidentialite: Confidentialite,
    
    Contact: Contact,
    
    Cookies: Cookies,
    
    Dashboard: Dashboard,
    
    Deadlines: Deadlines,
    
    Disclaimer: Disclaimer,
    
    Envelopes: Envelopes,
    
    Exports: Exports,
    
    FAQ: FAQ,
    
    Factures: Factures,
    
    Contrats: Contrats,
    Contrat: Contrat,
    ContratPublic: ContratPublic,

    DevisPublic: DevisPublic,
    
    Goals: Goals,
    
    Landing: Landing,

    Tarifs: Tarifs,
    
    MentionsLegales: MentionsLegales,

    Login: Login,

    CheckEmail: CheckEmail,

    Register: Register,

    AuthCallback: AuthCallback,

    ResetPassword: ResetPassword,

    Logout: Logout,
    
    Onboarding: Onboarding,
    
    PaymentSuccess: PaymentSuccess,
    
    Previsionnel: Previsionnel,
    
    Reports: Reports,
    
    Revenues: Revenues,
    LivreRecettes: LivreRecettes,
    
    Salaire: Salaire,
    
    Settings: Settings,
    
    Simulations: Simulations,
    
    Unsubscribe: Unsubscribe,
    
}

function _getCurrentPage(url) {
    // Root path should resolve to Landing (otherwise Layout may think it's a protected page)
    if (url === '/' || url === '') {
        return 'Landing';
    }

    // Remove query/hash
    url = String(url || '').split('?')[0].split('#')[0];

    // Trim trailing slash
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }

    // Special dynamic routes: /contrats/:id should still highlight 'Contrats'
    const lower = url.toLowerCase();
    if (lower.startsWith('/contrats/')) return 'Documents';
    if (lower.startsWith('/factures')) return 'Documents';

    // Legacy/marketing route: /pricing should behave like the in-app Billing page
    if (lower === '/pricing') return 'Billing';
    if (lower === '/support') return 'Contact';

    const segments = lower.split('/').filter(Boolean);
    if (segments.length === 0) return 'Landing';

    // Try to match by first segment (handles nested routes)
    const first = segments[0];
    const pageNameByFirst = Object.keys(PAGES).find((page) => page.toLowerCase() === first);
    if (pageNameByFirst) return pageNameByFirst;

    // Fallback: last segment
    const last = segments[segments.length - 1];
    const pageNameByLast = Object.keys(PAGES).find((page) => page.toLowerCase() === last);
    return pageNameByLast || 'Landing';
}

// Create a wrapper component that uses useLocation inside the Router context
export function AppRoutes() {
    return (
        <>
            <ScrollToTop />
            <TitleManager />
            <ReferralCapture />
            <Routes>
                {/* Public / SEO */}
                <Route path="/" element={<Landing />} />

                {/* Ambassadeur: lien court /a/CODE (stocke le code puis redirige vers Register) */}
                <Route path="/a/:code" element={<AmbassadorRedirect />} />
                <Route path="/amb/:code" element={<AmbassadorRedirect />} />

                {/* Contrats: lien client public */}
                <Route path="/c/:token" element={<ContratPublic />} />

                {/* Devis: lien client public */}
                <Route path="/d/:token" element={<DevisPublic />} />

                <Route path={createPageUrl('Tarifs')} element={<Tarifs />} />
                <Route path="/Tarifs" element={<Navigate to={createPageUrl('Tarifs')} replace />} />

                <Route path={createPageUrl('FAQ')} element={<FAQ />} />
                <Route path="/FAQ" element={<Navigate to={createPageUrl('FAQ')} replace />} />

                <Route path={createPageUrl('Contact')} element={<Contact />} />
                <Route path="/Contact" element={<Navigate to={createPageUrl('Contact')} replace />} />
                <Route path="/support" element={<Navigate to={createPageUrl('Contact')} replace />} />
                <Route path="/Support" element={<Navigate to={createPageUrl('Contact')} replace />} />

                <Route path={createPageUrl('MentionsLegales')} element={<MentionsLegales />} />
                <Route path="/MentionsLegales" element={<Navigate to={createPageUrl('MentionsLegales')} replace />} />

                <Route path={createPageUrl('CGU')} element={<CGU />} />
                <Route path="/CGU" element={<Navigate to={createPageUrl('CGU')} replace />} />

                <Route path={createPageUrl('Confidentialite')} element={<Confidentialite />} />
                <Route path="/Confidentialite" element={<Navigate to={createPageUrl('Confidentialite')} replace />} />

                <Route path={createPageUrl('Cookies')} element={<Cookies />} />
                <Route path="/Cookies" element={<Navigate to={createPageUrl('Cookies')} replace />} />

                <Route path={createPageUrl('Disclaimer')} element={<Disclaimer />} />
                <Route path="/Disclaimer" element={<Navigate to={createPageUrl('Disclaimer')} replace />} />

                {PROGRAMMATIC_PAGES.map((page) => (
                  <Route
                    key={page.path}
                    path={page.path}
                    element={<ProgrammaticPage page={page} />}
                  />
                ))}

                <Route path={createPageUrl('Unsubscribe')} element={<Unsubscribe />} />
                <Route path="/Unsubscribe" element={<Navigate to={createPageUrl('Unsubscribe')} replace />} />

                {/* Prefer root for the public landing (avoid relying on /landing on hosts without SPA rewrites) */}
                <Route path={createPageUrl('Landing')} element={<Navigate to="/" replace />} />
                <Route path="/Landing" element={<Navigate to="/" replace />} />

                {/* Legacy pricing route used by Stripe cancel_url in older sessions */}
                <Route path="/pricing" element={<Navigate to={createPageUrl('Billing')} replace />} />
                <Route path="/Pricing" element={<Navigate to={createPageUrl('Billing')} replace />} />

                {/* Auth redirect aliases (easier to whitelist in Supabase) */}
                <Route path="/auth-callback" element={<Navigate to={createPageUrl('AuthCallback')} replace />} />
                <Route path="/auth/callback" element={<Navigate to={createPageUrl('AuthCallback')} replace />} />
                <Route path="/reset-password" element={<Navigate to={createPageUrl('ResetPassword')} replace />} />
                <Route path="/update-password" element={<Navigate to={createPageUrl('ResetPassword')} replace />} />

                {/* Auth pages */}
                <Route path={createPageUrl('Login')} element={<Login />} />
                <Route path="/Login" element={<Navigate to={createPageUrl('Login')} replace />} />

                <Route path={createPageUrl('Register')} element={<Register />} />
                <Route path="/Register" element={<Navigate to={createPageUrl('Register')} replace />} />

                <Route path={createPageUrl('CheckEmail')} element={<CheckEmail />} />
                <Route path="/CheckEmail" element={<Navigate to={createPageUrl('CheckEmail')} replace />} />

                <Route path={createPageUrl('AuthCallback')} element={<AuthCallback />} />
                <Route path="/AuthCallback" element={<Navigate to={createPageUrl('AuthCallback')} replace />} />

                <Route path={createPageUrl('ResetPassword')} element={<ResetPassword />} />
                <Route path="/ResetPassword" element={<Navigate to={createPageUrl('ResetPassword')} replace />} />

                <Route path={createPageUrl('Logout')} element={<Logout />} />
                <Route path="/Logout" element={<Navigate to={createPageUrl('Logout')} replace />} />

                {/* Protected app */}
                <Route element={<ProtectedShell />}>
                  <Route path={createPageUrl('Dashboard')} element={<Dashboard />} />
                  <Route path={createPageUrl('Revenues')} element={<Revenues />} />
                  <Route path={createPageUrl('Deadlines')} element={<Deadlines />} />
                  <Route path={createPageUrl('Goals')} element={<Goals />} />
                  <Route path={createPageUrl('Documents')} element={<Documents />} />
                  <Route path={createPageUrl('Factures')} element={<Factures />} />
                  <Route path={createPageUrl('Contrats')} element={<Contrats />} />
                  <Route path="/contrats/:id" element={<Contrat />} />

                  <Route path={createPageUrl('Tresorerie')} element={<Tresorerie />} />
                  <Route path={createPageUrl('Charges')} element={<Charges />} />
                  <Route path={createPageUrl('Envelopes')} element={<Envelopes />} />
                  <Route path={createPageUrl('AIReports')} element={<AIReports />} />
                  <Route path={createPageUrl('Exports')} element={<Exports />} />

                  <Route path={createPageUrl('LivreRecettes')} element={<LivreRecettes />} />
                  <Route path={createPageUrl('Previsionnel')} element={<Previsionnel />} />
                  <Route path={createPageUrl('Simulations')} element={<Simulations />} />
                  <Route path={createPageUrl('Salaire')} element={<Salaire />} />
                  <Route path={createPageUrl('Reports')} element={<Reports />} />
                  <Route path={createPageUrl('Settings')} element={<Settings />} />
                  <Route path={createPageUrl('Billing')} element={<Billing />} />
                  <Route path={createPageUrl('PaymentSuccess')} element={<PaymentSuccess />} />
                  <Route path={createPageUrl('Onboarding')} element={<Onboarding />} />
                  <Route path={createPageUrl('AdminReminders')} element={<AdminReminders />} />
                </Route>

                {/* Convenience redirects (uppercase legacy URLs -> canonical lowercase URLs) */}
                <Route path="/Dashboard" element={<Navigate to={createPageUrl('Dashboard')} replace />} />
                <Route path="/Revenues" element={<Navigate to={createPageUrl('Revenues')} replace />} />
                <Route path="/Deadlines" element={<Navigate to={createPageUrl('Deadlines')} replace />} />
                <Route path="/Goals" element={<Navigate to={createPageUrl('Goals')} replace />} />
                <Route path="/Documents" element={<Navigate to={createPageUrl('Documents')} replace />} />
                <Route path="/Factures" element={<Navigate to={createPageUrl('Factures')} replace />} />
                <Route path="/Tresorerie" element={<Navigate to={createPageUrl('Tresorerie')} replace />} />
                <Route path="/Charges" element={<Navigate to={createPageUrl('Charges')} replace />} />
                <Route path="/Envelopes" element={<Navigate to={createPageUrl('Envelopes')} replace />} />
                <Route path="/AIReports" element={<Navigate to={createPageUrl('AIReports')} replace />} />
                <Route path="/Exports" element={<Navigate to={createPageUrl('Exports')} replace />} />
                <Route path="/LivreRecettes" element={<Navigate to={createPageUrl('LivreRecettes')} replace />} />
                <Route path="/Previsionnel" element={<Navigate to={createPageUrl('Previsionnel')} replace />} />
                <Route path="/Simulations" element={<Navigate to={createPageUrl('Simulations')} replace />} />
                <Route path="/Salaire" element={<Navigate to={createPageUrl('Salaire')} replace />} />
                <Route path="/Reports" element={<Navigate to={createPageUrl('Reports')} replace />} />
                <Route path="/Settings" element={<Navigate to={createPageUrl('Settings')} replace />} />
                <Route path="/Billing" element={<Navigate to={createPageUrl('Billing')} replace />} />
                <Route path="/PaymentSuccess" element={<Navigate to={createPageUrl('PaymentSuccess')} replace />} />
                <Route path="/Onboarding" element={<Navigate to={createPageUrl('Onboarding')} replace />} />
                <Route path="/AdminReminders" element={<Navigate to={createPageUrl('AdminReminders')} replace />} />

                {/* Legacy: Stripe cancel_url may still point to /pricing for old sessions */}
                <Route path="/pricing" element={<Navigate to={createPageUrl('Billing')} replace />} />
                <Route path="/Pricing" element={<Navigate to={createPageUrl('Billing')} replace />} />

                {/* Fallback: unknown routes -> home (prevents blank pages on bad links) */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
}

export default function Pages() {
    return (
        <Router>
            <AppRoutes />
        </Router>
    );
}